<!--
  Tracciabilità fornitori — redesigned 2026-06-09 (v2.5.0).

  Search page for the supplier traceability flow (end-to-end product
  tracking from supplier order → warehouse arrival → QC → expedition
  → delivery). User searches by product code or QR scan, optionally
  filters by date range; click on a result opens the 5-stage tracking
  page at /fornitori-tracking/:qr.

  TS was already modern (Router, FornitoriAdminMainService) — drop
  kendo-grid + kendo-datepicker only. Native `<input type="date">`
  with str getter/setter bridges. Palette $warn amber.
-->

<div class="trace-page">

  <!-- ─── Sticky header ────────────────────────────────────────── -->
  <header class="trace-header">
    <div class="trace-header__titles">
      <h1 class="trace-header__title">🔍 Tracciabilità prodotti</h1>
      <p class="trace-header__subtitle">
        @if (loading) { Ricerca in corso… }
        @else if (searched && results.length > 0) {
          {{ results.length }}
          {{ results.length === 1 ? 'risultato' : 'risultati' }}
          @if (results[0]?.product_qr) { · QR <strong>{{ results[0].product_qr }}</strong> }
        }
        @else if (searched && results.length === 0) { Nessun risultato }
        @else { Cerca per codice articolo, QR o range di date }
      </p>
    </div>
  </header>

  <!-- ─── Filter bar ───────────────────────────────────────────── -->
  <section class="trace-filters" aria-label="Filtri ricerca">
    <div class="filter-field">
      <label class="filter-field__label" for="searchTerm">Codice articolo o QR</label>
      <div class="filter-field__input-wrap">
        <input
          id="searchTerm"
          type="search"
          class="filter-field__input"
          placeholder="Es. ABC123 oppure $QR123…"
          [(ngModel)]="searchTerm"
          (keyup.enter)="search()"
        />
        @if (searchTerm) {
          <button
            type="button"
            class="filter-field__clear"
            (click)="searchTerm = ''"
            aria-label="Rimuovi filtro"
          >×</button>
        }
      </div>
    </div>

    <div class="filter-field filter-field--dates">
      <label class="filter-field__label">Range data</label>
      <div class="filter-field__date-pair">
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [(ngModel)]="startDateStr"
          aria-label="Data da"
        />
        <span class="filter-field__date-sep">→</span>
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [(ngModel)]="endDateStr"
          aria-label="Data a"
        />
        @if (startDate || endDate) {
          <button
            type="button"
            class="filter-field__clear"
            (click)="startDate = null; endDate = null"
            aria-label="Rimuovi filtri data"
          >×</button>
        }
      </div>
    </div>

    <div class="filter-actions">
      <button
        type="button"
        class="btn btn--primary"
        (click)="search()"
        [disabled]="loading || !filtersActive"
      >
        @if (loading) { <span class="btn-spinner"></span> }
        🔍 Cerca
      </button>
      @if (filtersActive) {
        <button
          type="button"
          class="btn btn--ghost"
          (click)="clearSearch()"
        >Pulisci</button>
      }
    </div>
  </section>

  <!-- ─── Advanced filter (cascade) — apre il modal legacy ──────── -->
  <!--
    Due modi di ricerca:
      1) Search bar sopra — quando l'utente conosce articolo completo
         (es. "ANSM12$3260$2026-00001") oppure il solo art_code.
      2) Filtri avanzati (questo bottone) — cascade articolo →
         fornitore → lotto → seriale, ogni selezione abilita la
         successiva e popola via API. Il backend supporta solo
         art_code + art_qr come input, quindi il cascade ricostruisce
         il QR completo via il modal e fa la ricerca server-side.
  -->
  <section class="trace-advanced" aria-label="Ricerca avanzata">
    <div class="trace-advanced__text">
      <span class="trace-advanced__title">🎯 Cerca per filtri</span>
      <span class="trace-advanced__hint">
        Non conosci il codice completo? Filtra per articolo, fornitore,
        lotto e seriale via dropdown cascade.
      </span>
    </div>
    <button
      type="button"
      class="btn btn--secondary"
      (click)="filterModalOpen = true"
    >
      🎛️ Apri filtri avanzati
    </button>
  </section>

  <!-- ─── Results area ─────────────────────────────────────────── -->
  <section class="trace-results-card" aria-label="Risultati ricerca">

    @if (loading) {
      <div class="trace-loading">
        <div class="trace-spinner"></div>
        <p class="trace-loading__msg">Caricamento risultati…</p>
      </div>
    } @else if (searchError) {
      <div class="trace-error">
        <div class="trace-error__icon" aria-hidden="true">⚠️</div>
        <p class="trace-error__title">Articolo non trovato</p>
        <p class="trace-error__subtitle">
          Verifica di aver scritto correttamente codice o QR e riprova.
        </p>
        <button type="button" class="btn btn--primary" (click)="retrySearch()">
          ↻ Riprova
        </button>
      </div>
    } @else if (!searched) {
      <div class="trace-empty">
        <div class="trace-empty__icon" aria-hidden="true">🔎</div>
        <p class="trace-empty__msg">
          Inserisci un codice articolo o un QR e premi <strong>Cerca</strong>.
        </p>
      </div>
    } @else if (results.length === 0) {
      <div class="trace-empty">
        <div class="trace-empty__icon" aria-hidden="true">📭</div>
        <p class="trace-empty__msg">
          Nessun articolo corrisponde ai filtri attuali.
        </p>
      </div>
    } @else {
      <div class="trace-table-scroll">
        <table class="trace-table" role="table">
          <thead>
            <tr>
              <th class="col-article">Articolo</th>
              <th class="col-order">Ordine fornitore</th>
              <th class="col-supplier-code">Numero fornitore</th>
              <th class="col-supplier-name">Nome fornitore</th>
              <th class="col-lot">Lotto</th>
              <th class="col-serial">N° seriale</th>
              <th class="col-serial">Descrizione</th>
              <th class="col-actions" aria-label="Apri tracking"></th>
            </tr>
          </thead>
          <tbody>
            @for (r of results; track r.product_qr + r.serial) {
              <tr
                class="trace-row"
                (click)="onOpenDetail(r)"
                title="Apri tracking completo"
              >
                <td class="col-article">
                  <span class="cell-article">{{ r.product_code || '—' }}</span>
                </td>
                <td class="col-order">
                  <span class="cell-order">{{ r.order_number || '—' }}</span>
                </td>
                <td class="col-supplier-code">
                  <span class="cell-supplier-code">{{ supplierCode(r.product_code) }}</span>
                </td>
                <td class="col-supplier-name">
                  <span class="cell-supplier-name">{{ supplierName(r.product_code) }}</span>
                </td>
                <td class="col-lot">
                  <span class="cell-lot">{{ formatLot(r.batch_year, r.batch_week) }}</span>
                </td>
                <td class="col-serial">
                  <span class="cell-serial">{{ r.serial || '—' }}</span>
                </td>
                <td class="col-serial">
                  <span class="cell-serial">{{ r.product_description || '—' }}</span>
                </td>
                <td class="col-actions" (click)="$event.stopPropagation()">
                  <!--<button
                    type="button"
                    class="icon-btn"
                    title="Apri tracking 5-stage"
                    (click)="onOpenDetail(r)"
                  ></button>-->
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>

</div>

<!-- Filter modal (column-level filtering) — preserved as-is -->
<app-tracciability-filter-modal
  [open]="filterModalOpen"
  [results]="results"
  [dateFrom]="startDate ? formatDate(startDate) : ''"
  [dateTo]="endDate ? formatDate(endDate) : ''"
  (closeModal)="filterModalOpen = false"
  (openDetail)="onOpenDetail($event)"
></app-tracciability-filter-modal>
